// =========================================================================
// Rendering a workflow step's body.
//
// The demo's `blocks()` returned an HTML string assembled by five helpers
// (`wfC`, `wfT`, `wfBar`, `wfNote`, plus two inline grids). `seed/workflow-
// steps.ts` ported all five into the `WorkflowBlock` discriminated union, so
// this file is the other half: a renderer for that union, and for the ten
// cell kinds inside a table row.
//
// | block     | demo helper                      | rendered as              |
// |-----------|----------------------------------|--------------------------|
// | `card`    | `wfC(title, sub, body)`          | a titled panel           |
// | `table`   | `wfT(cols, rows)`                | `table.t` in a scroller  |
// | `bars`    | `wfBar(rows)`                    | label · track · figure   |
// | `note`    | `wfNote(text)`                   | a sentence with links    |
// | `routes`  | `<div class="wfroute">`          | bordered panels          |
// | `quotes`  | `<div class="specline">`         | customer comments        |
//
// **No raw HTML is injected anywhere.** The demo built strings and set
// `innerHTML`; every fragment here is a real element, so React escapes the
// data and the source `esc()` calls are simply gone. That is the whole reason
// the seed models cells as values rather than as markup.
//
// Cards nest: `wfC`'s body is itself a block list, so `WorkflowBlocks`
// recurses. The demo only ever nested one level deep, but the union allows
// more and the renderer does not care.
//
// UI layer. Fork-owned (zone C), P5a lane.
// =========================================================================

import type { ReactNode } from 'react';
import { statusOf } from '@/versions/v1/shared/oem-engine';
import { SceneSection, SourceLink, TonePill } from '@/versions/v1/shared/oem-tiles';
import type {
  WorkflowBar,
  WorkflowBlock,
  WorkflowCell,
  WorkflowNote,
  WorkflowQuote,
  WorkflowRoute,
} from '@/versions/v1/shared/seed/workflow-steps';
import type { SeedNav } from '@/versions/v1/shared/seed/modules';

/** What the renderer needs from the scene: how to act on a link. */
export interface BlockActions {
  /** Run a `SeedNav` — a route, a drawer, a document or a question. */
  readonly dispatch: (nav: SeedNav) => void;
  /** Open a machine page. */
  readonly openMachine: (machineId: string) => void;
}

/** The section colour, for the route panels' left rail. */
export interface BlockTheme {
  readonly colour: string;
}

export function WorkflowBlocks({
  blocks,
  actions,
  theme,
}: {
  blocks: readonly WorkflowBlock[];
  actions: BlockActions;
  theme: BlockTheme;
}) {
  return (
    <>
      {blocks.map((block, i) => (
        <Block key={blockKey(block, i)} block={block} actions={actions} theme={theme} />
      ))}
    </>
  );
}

/**
 * A stable key for a block.
 *
 * A card has a title; the other five kinds have nothing unique, and two
 * sibling tables inside one card are genuinely indistinguishable, so the
 * index is the honest key for them.
 */
function blockKey(block: WorkflowBlock, i: number): string {
  return block.kind === 'card' ? `card:${block.title}` : `${block.kind}:${i}`;
}

function Block({
  block,
  actions,
  theme,
}: {
  block: WorkflowBlock;
  actions: BlockActions;
  theme: BlockTheme;
}) {
  switch (block.kind) {
    case 'card':
      return (
        <SceneSection title={block.title} subtitle={block.sub}>
          <div className="space-y-3">
            <WorkflowBlocks blocks={block.body} actions={actions} theme={theme} />
          </div>
        </SceneSection>
      );

    case 'table':
      return <BlockTable block={block} actions={actions} />;

    case 'bars':
      return <BlockBars rows={block.rows} />;

    case 'note':
      return <BlockNote text={block.text} actions={actions} />;

    case 'routes':
      return <BlockRoutes routes={block.routes} actions={actions} colour={theme.colour} />;

    case 'quotes':
      return <BlockQuotes lines={block.lines} />;
  }
}

/* ------------------------------------------------------------------ *
 * table — `wfT(cols, rows)`
 * ------------------------------------------------------------------ */

function BlockTable({
  block,
  actions,
}: {
  block: Extract<WorkflowBlock, { kind: 'table' }>;
  actions: BlockActions;
}) {
  return (
    <div
      className="overflow-x-auto rounded-xl border"
      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
    >
      <table className="w-full border-collapse text-left">
        <thead>
          <tr>
            {block.cols.map((col, i) => (
              <th
                key={`${col.label}:${i}`}
                scope="col"
                className={`whitespace-nowrap px-[9px] py-[7px] text-[10.5px] font-semibold uppercase tracking-[0.06em] ${
                  col.numeric ? 'text-right' : ''
                }`}
                style={{ color: 'var(--ink-tertiary)', borderBottom: '1px solid var(--hairline)' }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row, r) => (
            <tr key={rowKey(row, r)}>
              {row.map((cell, c) => (
                <td
                  key={c}
                  className={`px-[9px] py-1.5 align-top text-[12px] ${
                    block.cols[c]?.numeric ? 'mono text-right' : ''
                  }`}
                  style={{
                    color: 'var(--ink-primary)',
                    borderTop: r ? '1px solid var(--hairline)' : undefined,
                  }}
                >
                  <Cell cell={cell} actions={actions} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * A stable key for a table row.
 *
 * Built from the row's own leading cells rather than from a single id column,
 * because these tables have no id column in general and several of them
 * legitimately repeat a value in the first column — a machine with two
 * components, a part at two sites. Position is appended so a genuinely
 * duplicated row still gets a unique key.
 */
function rowKey(row: readonly WorkflowCell[], r: number): string {
  const lead = row
    .slice(0, 2)
    .map((cell) => (typeof cell === 'string' ? cell : cellText(cell)))
    .join('|');
  return `${lead}|${r}`;
}

/** The text a non-string cell carries, for keying only. */
function cellText(cell: Exclude<WorkflowCell, string>): string {
  switch (cell.kind) {
    case 'machine':
      return cell.id;
    case 'machines':
      return cell.ids.join(',');
    case 'meter':
      return String(cell.pct);
    case 'link':
      return cell.label;
    case 'check':
      return cell.text;
    default:
      return cell.text;
  }
}

/* ------------------------------------------------------------------ *
 * cells
 * ------------------------------------------------------------------ */

function Cell({ cell, actions }: { cell: WorkflowCell; actions: BlockActions }) {
  if (typeof cell === 'string') return <>{cell}</>;

  switch (cell.kind) {
    case 'machine':
      return (
        <span className="inline-flex items-center">
          {cell.band ? <BandDot machineId={cell.id} /> : null}
          <MachineLink id={cell.id} onSelect={actions.openMachine} />
        </span>
      );

    case 'machines':
      return (
        <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          {cell.ids.map((id, i) => (
            <MachineLink key={`${id}:${i}`} id={id} onSelect={actions.openMachine} />
          ))}
        </span>
      );

    case 'mono':
      return <span className="mono text-[11.5px]">{cell.text}</span>;

    case 'pill':
      return <TonePill tone={pillTone(cell.tone)}>{cell.text}</TonePill>;

    case 'emphasis':
      return (
        <b
          className="font-semibold"
          style={cell.tone ? { color: `var(--band-${cell.tone})` } : undefined}
        >
          {cell.text}
        </b>
      );

    case 'meter':
      return <Meter pct={cell.pct} />;

    case 'link':
      return <SourceLink onClick={() => actions.dispatch(cell.nav)}>{cell.label}</SourceLink>;

    case 'check':
      return (
        <span
          className="whitespace-nowrap text-[11px]"
          style={{ color: cell.ok ? 'var(--band-good)' : 'var(--band-vpoor)' }}
        >
          {cell.ok ? '✓' : '✗'} {cell.text}
        </span>
      );

    case 'dim':
      return (
        <span className="text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
          {cell.text}
        </span>
      );
  }
}

/**
 * The seed's five pill tones onto the four the tile vocabulary has.
 *
 * `vpoor` is the condition band, not a tone word; it and `warn` both read as
 * "this needs attention", so they land on the same chip rather than inventing
 * a fifth.
 */
const pillTone = (tone: string) =>
  tone === 'good' ? 'good' : tone === 'vpoor' ? 'bad' : tone === 'mute' ? 'flat' : 'warn';

/** `bandDot(id)` — the machine's worst condition band, as a 7px dot. */
function BandDot({ machineId }: { machineId: string }) {
  const band = statusOf(machineId);
  return <span className={`band-dot band-${band} mr-1.5`} aria-hidden="true" />;
}

/** `mLink(id)`. */
function MachineLink({ id, onSelect }: { id: string; onSelect: (id: string) => void }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(id)}
      className="mono rounded text-[11.5px] font-medium underline decoration-dotted underline-offset-[3px] transition-colors hover:decoration-solid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
      style={{ color: 'var(--accent)' }}
    >
      {id}
    </button>
  );
}

/** The inline planned-vs-unplanned ratio bar in the `plan` step. */
function Meter({ pct }: { pct: number }) {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        role="img"
        aria-label={`${Math.round(clamped)} percent`}
        className="inline-block h-1.5 w-16 overflow-hidden rounded-full align-middle"
        style={{ background: 'var(--surface-3)' }}
      >
        <span
          className="block h-full rounded-full"
          style={{ width: `${clamped}%`, background: 'var(--accent)' }}
        />
      </span>
      <span className="mono text-[11px]" style={{ color: 'var(--ink-tertiary)' }}>
        {Math.round(clamped)}%
      </span>
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * bars — `wfBar(rows)`
 * ------------------------------------------------------------------ */

function BlockBars({ rows }: { rows: readonly WorkflowBar[] }) {
  // The demo scaled every bar against the largest value in the set, with a
  // divide-by-zero guard (`|| 1`). Kept: a set of all-zero rows renders as
  // empty tracks rather than as `NaN%` widths.
  const max = Math.max(...rows.map((r) => r.value), 0) || 1;

  return (
    <div className="grid gap-[5px] text-[12px]">
      {rows.map((row, i) => (
        <div
          key={`${row.label}:${i}`}
          className="grid grid-cols-[minmax(110px,170px)_minmax(0,1fr)_auto] items-center gap-2.5"
        >
          <span className="truncate" style={{ color: 'var(--ink-secondary)' }}>
            {row.label}
          </span>
          <span
            className="h-2.5 overflow-hidden rounded-[3px]"
            style={{ background: 'var(--surface-3)' }}
            aria-hidden="true"
          >
            <span
              className="block h-full"
              style={{
                width: `${((row.value / max) * 100).toFixed(1)}%`,
                background: row.colour ?? 'var(--accent)',
              }}
            />
          </span>
          <span
            className="mono whitespace-nowrap text-[11px]"
            style={{ color: 'var(--ink-tertiary)' }}
          >
            {row.text}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * note — `wfNote(text)`
 * ------------------------------------------------------------------ */

function BlockNote({ text, actions }: { text: WorkflowNote; actions: BlockActions }) {
  return (
    <p className="text-[12px] leading-[1.55]" style={{ color: 'var(--ink-tertiary)' }}>
      {text.map((segment, i) =>
        segment.kind === 'text' ? (
          <span key={i}>{segment.text}</span>
        ) : (
          <SourceLink key={i} onClick={() => actions.dispatch(segment.nav)}>
            {segment.label}
          </SourceLink>
        ),
      )}
    </p>
  );
}

/* ------------------------------------------------------------------ *
 * routes — the `wfroute` grid
 * ------------------------------------------------------------------ */

function BlockRoutes({
  routes,
  actions,
  colour,
}: {
  routes: readonly WorkflowRoute[];
  actions: BlockActions;
  colour: string;
}) {
  return (
    <div className="grid grid-cols-1 gap-2.5 @2xl:grid-cols-2 @5xl:grid-cols-3">
      {routes.map((route) => (
        <div
          key={route.title}
          className="flex min-w-0 flex-col items-start gap-1 rounded-[9px] border p-3"
          style={{
            borderColor: 'var(--hairline)',
            borderLeft: `3px solid ${colour}`,
            background: 'var(--surface-2)',
          }}
        >
          <b className="text-[12.5px] font-semibold" style={{ color: 'var(--ink-primary)' }}>
            {route.title}
          </b>
          <span className="text-[11.5px] leading-[1.4]" style={{ color: 'var(--ink-tertiary)' }}>
            {route.text}
          </span>
          {route.link ? (
            <SourceLink onClick={() => actions.dispatch(route.link!.nav)}>
              {route.link.label}
            </SourceLink>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * quotes — the `specline` customer comments
 * ------------------------------------------------------------------ */

function BlockQuotes({ lines }: { lines: readonly WorkflowQuote[] }) {
  return (
    <ul className="grid gap-0">
      {lines.map((line, i) => (
        <li
          key={`${line.site}:${line.contact}:${i}`}
          className="relative py-1.5 pl-3.5 text-[12px] leading-[1.45]"
          style={{
            color: 'var(--ink-secondary)',
            borderTop: i ? '1px solid var(--hairline)' : undefined,
          }}
        >
          <span
            aria-hidden="true"
            className="absolute left-0 top-[7px] text-[9px]"
            style={{ color: 'var(--accent)' }}
          >
            ▸
          </span>
          <b className="font-semibold" style={{ color: 'var(--ink-primary)' }}>
            {line.site}
          </b>
          {' · '}
          {line.contact}
          {' · '}
          <span className="mono text-[11.5px]">{line.machine}</span>
          {' — '}
          <span style={{ color: 'var(--ink-tertiary)' }}>“{line.comment}”</span>
        </li>
      ))}
    </ul>
  );
}

/** Re-exported so the scene can type its own wrapper without reaching in. */
export type { ReactNode };
